import { getAuthHeaders, API_BASE_URL } from './pokemonCardOwnershipService';
import { getAccessToken } from './authService';

export interface StoreListing {
  listingId: string;
  sellerId: string;
  sellerName: string;
  sellerEmail: string;
  cardId: string;
  setId: string;
  game: 'pokemon' | 'onepiece';
  price: number;
  stockQuantity: number;
  condition: string;
  language: string;
  imageUrl: string;
  cardName: string;
  number: string;
  setName: string;
  rarity?: string;
  status: 'active' | 'draft';
  cardOwner: string; // Consignment partner name
  priceType?: 'fixed' | 'market'; // Dynamic price binding
  consignmentRate?: number; // Store fee percentage (e.g. 10)
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  listingId: string;
  cardId: string;
  setId: string;
  game: 'pokemon' | 'onepiece';
  cardName: string;
  imageUrl: string;
  condition: string;
  price: number;
  quantity: number;
  cardOwner: string; // Partner name captured at completed checkout
  consignmentRate?: number; // Store commission rate locked at purchase
}

export interface StoreOrder {
  orderId: string;
  sellerId: string;
  buyerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'ready' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

/**
 * Helper to get optional auth headers for public requests (attaches token if logged in)
 */
async function getOptionalAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// ==========================================
// Public Storefront APIs
// ==========================================

export async function getPublicStoreInventory(filters?: {
  game?: string;
  search?: string;
}): Promise<StoreListing[]> {
  try {
    const params = new URLSearchParams();
    if (filters?.game) params.append('game', filters.game);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = `${API_BASE_URL}/api/public/store/inventory${queryString ? `?${queryString}` : ''}`;
    const headers = await getOptionalAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch public store listings');
    }
    const data = await response.json();
    return data.listings || [];
  } catch (error) {
    console.error('Error in getPublicStoreInventory:', error);
    throw error;
  }
}

export async function createPickupOrder(orderInput: {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  items: Array<{ listingId: string; quantity: number }>;
}): Promise<StoreOrder> {
  try {
    const url = `${API_BASE_URL}/api/public/store/orders`;
    const headers = await getOptionalAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(orderInput),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to place pickup order');
    }
    return data.order;
  } catch (error) {
    console.error('Error in createPickupOrder:', error);
    throw error;
  }
}

// ==========================================
// Store Admin APIs (Protected)
// ==========================================

export async function getSellerInventory(): Promise<StoreListing[]> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/inventory`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch seller inventory');
    }
    const data = await response.json();
    return data.listings || [];
  } catch (error) {
    console.error('Error in getSellerInventory:', error);
    throw error;
  }
}

export async function createListing(listingInput: Omit<StoreListing, 'listingId' | 'sellerId' | 'sellerName' | 'sellerEmail' | 'createdAt' | 'updatedAt'>): Promise<StoreListing> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/inventory`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(listingInput),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create listing');
    }
    return data.listing;
  } catch (error) {
    console.error('Error in createListing:', error);
    throw error;
  }
}

export async function updateListing(
  listingId: string,
  updates: Partial<Omit<StoreListing, 'listingId' | 'sellerId' | 'sellerName' | 'sellerEmail' | 'createdAt' | 'updatedAt'>>
): Promise<StoreListing> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/inventory/${listingId}`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update listing');
    }
    return data.listing;
  } catch (error) {
    console.error(`Error in updateListing for ${listingId}:`, error);
    throw error;
  }
}

export async function deleteListing(listingId: string): Promise<void> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/inventory/${listingId}`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to delete listing');
    }
  } catch (error) {
    console.error(`Error in deleteListing for ${listingId}:`, error);
    throw error;
  }
}

export async function getSellerOrders(): Promise<StoreOrder[]> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/orders`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error('Failed to fetch seller orders');
    }
    const data = await response.json();
    return data.orders || [];
  } catch (error) {
    console.error('Error in getSellerOrders:', error);
    throw error;
  }
}

export async function updateOrderStatus(
  orderId: string,
  status: 'pending' | 'ready' | 'completed' | 'cancelled'
): Promise<StoreOrder> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/orders/${orderId}/status`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ status }),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update order status');
    }
    return data.order;
  } catch (error) {
    console.error(`Error in updateOrderStatus for ${orderId}:`, error);
    throw error;
  }
}

export interface PartnerLedgerRow {
  partnerName: string;
  activeValue: number;
  activeCount: number;
  owedPayout: number;
  completedCount: number;
}

export async function getPartnerLedger(): Promise<PartnerLedgerRow[]> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/partners/ledger`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load partner payouts ledger: ${response.status} - ${errorText}`);
    }
    const data = await response.json();
    return data.ledger || [];
  } catch (error) {
    console.error('Error in getPartnerLedger:', error);
    throw error;
  }
}

export interface SalesPeriodSummary {
  grossSales: number;
  consignmentCost: number;
  netRevenue: number;
  orderCount: number;
}

export interface SalesAnalyticsResponse {
  summary: {
    month: SalesPeriodSummary;
    year: SalesPeriodSummary;
    lifetime: SalesPeriodSummary;
  };
  monthlyTrend: Array<{
    month: string;
    grossSales: number;
    consignmentCost: number;
    netRevenue: number;
    orderCount: number;
  }>;
  orders: StoreOrder[];
}

export async function getSalesAnalytics(): Promise<SalesAnalyticsResponse> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/analytics/sales`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load sales analytics: ${response.status} - ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error in getSalesAnalytics:', error);
    throw error;
  }
}

export interface StoreUser {
  username: string;
  email: string;
  groups: string[];
}

export async function getStoreUsers(): Promise<StoreUser[]> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/users`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, { headers });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to load users: ${response.status} - ${errorText}`);
    }
    const data = await response.json();
    return data.users || [];
  } catch (error) {
    console.error('Error in getStoreUsers:', error);
    throw error;
  }
}

export async function updateStoreUserRole(username: string, role: string): Promise<void> {
  try {
    const url = `${API_BASE_URL}/api/store/admin/users/${username}/role`;
    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ role }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update user role: ${response.status} - ${errorText}`);
    }
  } catch (error) {
    console.error('Error in updateStoreUserRole:', error);
    throw error;
  }
}
